import { useRef, useCallback, useState, useEffect } from 'react';
import { Loader2, Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AgentIcon } from '@/components/common/AgentIcon';
import { cn } from '@/lib/utils';
import { useToast } from '@/context/ToastContext';
import { rpcClient } from '@/api/rpc/client';
import type { AudioStreamEvent } from '@erp/rpc';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (messageOverride?: string) => void;
  isLoading: boolean;
  onCancel?: () => void;
  placeholder?: string;
  className?: string;
  textareaClassName?: string;
  size?: 'sm' | 'default';
  enableVoiceInput?: boolean;
  onVoiceTranscription?: (text: string | null) => void;
  /** Auto-focus the input when this becomes true */
  autoFocus?: boolean;
}

export function ChatInput({
  value,
  onChange,
  onSend,
  isLoading,
  onCancel,
  placeholder = 'Type a message...',
  className,
  textareaClassName,
  size = 'default',
  enableVoiceInput = false,
  onVoiceTranscription,
  autoFocus = false,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus when autoFocus prop becomes true
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      // Small delay to ensure the panel has rendered
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);
  const [recording, setRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const transcribeAbortRef = useRef(false);
  const currentTranscriptRef = useRef<string>('');
  const sessionIdRef = useRef(0);
  const activeSessionRef = useRef<number | null>(null);
  // Ref to always access the latest onSend callback, avoiding stale closures in recorder.onstop
  const onSendRef = useRef(onSend);
  const { showToast } = useToast();

  useEffect(() => {
    onSendRef.current = onSend;
  }, [onSend]);

  useEffect(() => {
    return () => {
      transcribeAbortRef.current = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleSend = useCallback(() => {
    if (!value.trim() || isLoading) return;
    onSend();
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [value, isLoading, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const transcribeAudio = async (audioBlob: Blob, sessionId: number) => {
    if (transcribeAbortRef.current) return;

    try {
      const arrayBuffer = await audioBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const chunkSize = 8192;
      let binaryString = '';
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.subarray(i, i + chunkSize);
        binaryString += String.fromCharCode.apply(
          null,
          chunk as unknown as number[],
        );
      }
      const base64Audio = btoa(binaryString);

      console.log('[ChatInput] Sending audio for streaming transcription...');

      const response = await rpcClient.ai.streamingAudio.streamAudioInput({
        audioData: base64Audio,
        config: {
          sampleRate: 16000,
          channels: 1,
          enableVAD: true,
          vadSensitivity: 'medium',
          language: 'en',
        },
      });

      for await (const event of response) {
        if (
          transcribeAbortRef.current ||
          activeSessionRef.current !== sessionId
        )
          break;
        handleStreamEvent(event, sessionId);
      }
    } catch (error) {
      if (transcribeAbortRef.current) return;
      console.error('[ChatInput] Transcription error:', error);
    } finally {
      if (
        !transcribeAbortRef.current &&
        activeSessionRef.current === sessionId
      ) {
        setIsTranscribing(false);
      }
    }
  };

  const handleStreamEvent = (event: AudioStreamEvent, sessionId: number) => {
    if (activeSessionRef.current !== sessionId) return;

    switch (event.type) {
      case 'transcription-delta':
        currentTranscriptRef.current = event.text;
        onVoiceTranscription?.(event.text);
        console.log(
          '[ChatInput] Transcription delta:',
          event.text.slice(-50),
          event.isFinal ? '(final)' : '(partial)',
        );
        break;
      case 'complete':
        console.log('[ChatInput] Transcription complete:', event.finalText);
        if (event.finalText) {
          currentTranscriptRef.current = event.finalText;
          onVoiceTranscription?.(event.finalText);
        }
        setIsTranscribing(false);
        break;
      case 'error':
        console.error('[ChatInput] Stream error:', event.message);
        setIsTranscribing(false);
        break;
    }
  };

  const startRecording = async () => {
    if (isLoading || recording) return;

    setRecording(true);
    onVoiceTranscription?.('');

    try {
      const currentSession = ++sessionIdRef.current;
      activeSessionRef.current = currentSession;
      transcribeAbortRef.current = false;
      chunksRef.current = [];
      currentTranscriptRef.current = '';

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });

      recorder.ondataavailable = async (e) => {
        if (
          e.data &&
          e.data.size > 0 &&
          activeSessionRef.current === currentSession
        ) {
          chunksRef.current.push(e.data);

          const cumulativeBlob = new Blob(chunksRef.current, {
            type: recorder.mimeType || 'audio/webm',
          });

          console.log(
            '[ChatInput] Audio chunk received, cumulative size:',
            cumulativeBlob.size,
          );

          if (cumulativeBlob.size > 1000) {
            setIsTranscribing(true);
            transcribeAudio(cumulativeBlob, currentSession);
          }
        }
      };

      recorder.onstop = async () => {
        console.log('[ChatInput] Recording stopped');

        if (
          chunksRef.current.length > 0 &&
          activeSessionRef.current === currentSession
        ) {
          const finalBlob = new Blob(chunksRef.current, {
            type: recorder.mimeType || 'audio/webm',
          });

          if (finalBlob.size > 1000) {
            setIsTranscribing(true);
            await transcribeAudio(finalBlob, currentSession);
          }
        }

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        if (activeSessionRef.current === currentSession) {
          const finalText = currentTranscriptRef.current.trim();
          if (finalText) {
            console.log(
              '[ChatInput] Auto-sending transcribed message:',
              finalText,
            );
            onSendRef.current(finalText);
          }

          activeSessionRef.current = null;
          onVoiceTranscription?.(null);
          currentTranscriptRef.current = '';
        }
      };

      const CHUNK_INTERVAL_MS = 1500;
      recorder.start(CHUNK_INTERVAL_MS);
      mediaRecorderRef.current = recorder;
      console.log('[ChatInput] Recording started');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      showToast({
        title: 'Microphone Error',
        message:
          'Could not access microphone. Please check your browser permissions.',
        color: 'error',
      });
      cleanup();
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      console.log('[ChatInput] Stopping recording...');
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    setRecording(false);
  };

  const cleanup = () => {
    transcribeAbortRef.current = true;
    activeSessionRef.current = null;
    setRecording(false);
    setIsTranscribing(false);
    onVoiceTranscription?.(null);
    currentTranscriptRef.current = '';
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop();
      } catch (_) {}
      mediaRecorderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    chunksRef.current = [];
  };

  const handleMicMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setTooltipOpen(false);
    if (!isLoading && !recording) {
      startRecording();
    }
  };

  const handleMicMouseUp = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (recording) {
      stopRecording();
    }
  };

  const handleMicMouseLeave = () => {
    if (recording) {
      stopRecording();
    }
  };

  const sizeStyles = {
    sm: {
      textarea: 'min-h-[50px] max-h-[80px] text-sm',
      icon: 20,
    },
    default: {
      textarea: 'min-h-[60px] max-h-[120px]',
      icon: 24,
    },
  };

  const styles = sizeStyles[size];

  const micButton = (
    <Button
      onMouseDown={handleMicMouseDown}
      onMouseUp={handleMicMouseUp}
      onMouseLeave={handleMicMouseLeave}
      onTouchStart={handleMicMouseDown}
      onTouchEnd={handleMicMouseUp}
      disabled={isLoading}
      variant={recording ? 'destructive' : 'outline'}
      className={cn(
        'h-auto px-3 select-none',
        recording && 'animate-pulse',
        isTranscribing && !recording && 'opacity-70',
      )}
      style={recording ? { backgroundColor: '#dc2626' } : undefined}
      aria-label={recording ? 'Release to send' : 'Hold to speak'}
    >
      {isTranscribing && !recording ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Mic className="h-4 w-4" />
      )}
    </Button>
  );

  return (
    <div className={cn('flex gap-2', className)}>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={recording ? 'Listening...' : placeholder}
        className={cn('resize-none', styles.textarea, textareaClassName)}
        disabled={isLoading || recording}
      />
      <div className="flex flex-col gap-2">
        {isLoading ? (
          onCancel ? (
            <Button
              onClick={onCancel}
              variant="secondary"
              className="h-auto px-3"
              aria-label="Cancel"
            >
              <AgentIcon state="thinking" size={styles.icon} />
            </Button>
          ) : (
            <Button variant="secondary" className="h-auto px-3" disabled>
              <Loader2 className="h-4 w-4 animate-spin" />
            </Button>
          )
        ) : (
          <Button
            onClick={handleSend}
            disabled={!value.trim() || recording}
            className="h-auto px-3 bg-stone-700 hover:bg-stone-800 text-stone-50"
            aria-label="Send message"
          >
            <AgentIcon state="send" size={styles.icon} />
          </Button>
        )}
        {enableVoiceInput && (
          <Tooltip
            open={tooltipOpen && !recording}
            onOpenChange={setTooltipOpen}
          >
            <TooltipTrigger asChild>{micButton}</TooltipTrigger>
            <TooltipContent side="left">Hold to speak</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
